import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Theme, ToastNotification } from '@carbon/react';
import {
  CheckboxChecked,
  Checkmark,
  ChevronDown,
  ChevronRight,
  Close,
  Group as GroupIcon,
  Person,
  Restaurant,
  Search as SearchIcon,
  Task as TaskIcon,
  Time,
} from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import { getApiErrorMessage, isConcurrencyConflictError } from '../../utils/apiErrors';
import { useTheme } from '../../contexts/ThemeContext';
import Collapsible from '../Collapsible';
import {
  formatTime,
  getResidentName,
  getRoomName,
  getTaskSubtitle,
  getTaskTitle,
  pluralForm,
} from './workDayUtils';
import TaskDetailsDrawer from './TaskDetailsDrawer';
import './WorkDayDashboard.css';

const TABS = [
  { id: 'mine', label: 'Moji' },
  { id: 'available', label: 'Dostupni' },
  { id: 'completed', label: 'Završeni' },
];

// ===== Normalizacija odgovora (isti formati kao u CaregiverTasks) =====
const groupTasks = (list) => {
  const groups = {
    assignedToMe: [],
    unassigned: [],
    assignedToMeInPast: [],
    myCompletedTasks: [],
    myCanceledTasks: [],
  };
  const now = Date.now();
  (Array.isArray(list) ? list : []).forEach((task) => {
    const status = (task.status || '').toLowerCase();
    if (status === 'canceled') {
      groups.myCanceledTasks.push(task);
    } else if (status === 'completed') {
      groups.myCompletedTasks.push(task);
    } else if (!task.assignedToId) {
      groups.unassigned.push(task);
    } else {
      const t = task.scheduledTime ? new Date(task.scheduledTime).getTime() : NaN;
      if (!Number.isNaN(t) && t < now) {
        groups.assignedToMeInPast.push(task);
      } else {
        groups.assignedToMe.push(task);
      }
    }
  });
  return groups;
};

const normalizeGroup = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    if (value.every((item) => item && Array.isArray(item.tasks))) {
      return value.map((group) => ({
        category: group.category || null,
        count: typeof group.count === 'number' ? group.count : group.tasks.length,
        tasks: group.tasks,
      }));
    }
    return [{ category: null, count: value.length, tasks: value }];
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .map((category) => ({
        category,
        tasks: Array.isArray(value[category]) ? value[category] : [],
      }))
      .filter((entry) => entry.tasks.length > 0);
  }
  return [];
};

const GROUP_KEYS = ['assignedToMeInPast', 'assignedToMe', 'unassigned', 'myCompletedTasks', 'myCanceledTasks'];

const normalizeData = (payload) => {
  const result = {
    assignedToMeInPast: [],
    assignedToMe: [],
    unassigned: [],
    myCompletedTasks: [],
    myCanceledTasks: [],
  };
  if (!payload) return result;
  if (Array.isArray(payload)) {
    const grouped = groupTasks(payload);
    GROUP_KEYS.forEach((key) => {
      result[key] = normalizeGroup(grouped[key]);
    });
    return result;
  }
  GROUP_KEYS.forEach((key) => {
    result[key] = normalizeGroup(payload[key]);
  });
  return result;
};

// Grupe [{category, tasks}] -> ravna lista zadataka sa pridruženom kategorijom
const flattenEnriched = (entries) =>
  (Array.isArray(entries) ? entries : []).flatMap((entry) =>
    (Array.isArray(entry.tasks) ? entry.tasks : []).map((task) => ({
      ...task,
      category: entry.category || null,
    }))
  );

// Ravna lista -> grupe po kategoriji (redosled prvog pojavljivanja)
const regroup = (tasks) => {
  const map = new Map();
  tasks.forEach((task) => {
    const key = task.category || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(task);
  });
  return Array.from(map.entries()).map(([category, list]) => ({
    category: category || null,
    tasks: list,
  }));
};

// Ključ kategorije u listi zadataka (koristi se i za collapse stanje kategorije).
const categoryKey = (group, index) => group.category || `__all-${index}`;

// Prikaz polja zadatka (naslov, podnaslov, ime korisnika, soba) izdvojen je u
// `workDayUtils` jer ih koristi i desni panel sa detaljima zadatka.

// Ikona kategorije na osnovu naziva (npr. "Ručak", "Lična nega", "Popodnevne aktivnosti")
const categoryIcon = (category) => {
  const value = String(category || '').toLowerCase();
  if (/ručak|dor[uč]ak|večera|obrok|ishran|jelo|užin|kaf|čaj/.test(value)) return Restaurant;
  if (/nega|higijen|kupan|oblačen|ličn|medicin|terapij|lek/.test(value)) return Person;
  if (/aktivnost|družen|popodnev|šetnj|grupn|vežb|fizio/.test(value)) return GroupIcon;
  return TaskIcon;
};

// Jedan red zadatka (memoizovan da selekcija ne re-renderuje celu listu).
// Klik (ili Enter/Space) na red otvara desni panel sa detaljima zadatka.
const TaskRow = React.memo(function TaskRow({ task, selectable, selected, active, onToggle, onOpen }) {
  const title = getTaskTitle(task);
  const subtitle = getTaskSubtitle(task);
  const hasTime = Boolean(task?.scheduledTime);

  const openDetails = () => {
    if (onOpen) onOpen(task);
  };

  return (
    <div
      className={`wdash-task ${selected ? 'wdash-task--selected' : ''} ${
        active ? 'wdash-task--active' : ''
      }`}
      role="button"
      tabIndex={0}
      title="Prikaži detalje zadatka"
      onClick={openDetails}
      onKeyDown={(event) => {
        // Tasteri pritisnuti na dečijim kontrolama (checkbox) ne otvaraju detalje
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDetails();
        }
      }}
    >
      {selectable ? (
        <button
          type="button"
          className={`wdash-task__check ${selected ? 'is-checked' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(task.id);
          }}
          onKeyDown={(event) => event.stopPropagation()}
          aria-pressed={selected}
          aria-label={selected ? 'Poništi izbor zadatka' : 'Odaberi zadatak'}
        >
          {selected && <Checkmark size={14} />}
        </button>
      ) : (
        <span className="wdash-task__check wdash-task__check--done" aria-hidden="true">
          <Checkmark size={14} />
        </span>
      )}

      <div className="wdash-task__text">
        <span className="wdash-task__title">{title}</span>
        {subtitle && <span className="wdash-task__sub">{subtitle}</span>}
      </div>

      <div className="wdash-task__end">
        {hasTime && (
          <span className="wdash-task__time" title="Vreme izvršavanja">
            <Time size={14} />
            {formatTime(task.scheduledTime)}
          </span>
        )}
        <ChevronRight size={16} className="wdash-task__chevron" />
      </div>
    </div>
  );
});

/**
 * WorkDayTasks - "Zadaci danas": tabovi (Moji / Dostupni / Završeni), filteri
 * (oblast, tip zadatka, pretraga korisnika) i grupisan spisak zadataka sa
 * mogućnošću preuzimanja (dodele) ili završavanja.
 */
const WorkDayTasks = ({ endpoint = '/api/Task/caregiver-tasks' }) => {
  const { theme } = useTheme();

  // Da li je PC prikaz (>= 769px). Na PC-u su lebdeći dugmići ("Označi sve" i
  // "Preuzmi/Završi") vidljivi stalno, a na mobilnom se prikazuju uz selekciju.
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 769px)').matches
      : true
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(min-width: 769px)');
    const handleChange = (event) => setIsDesktopView(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Stariji browseri (Safari < 14)
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  const searchInputRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('available');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  // Razvijene kategorije zadataka. Prazan skup = SVE kategorije su sklopljene,
  // što je i podrazumevano stanje panela "Zadaci danas".
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  // Da li je ceo panel "Zadaci danas" sklopljen (collapse/expand preko zaglavlja)
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  // Izabrani zadatak za desni panel sa detaljima (klik na red zadatka)
  const [detailsTask, setDetailsTask] = useState(null);

  const showToast = useCallback((kind, title, subtitle) => {
    setToast({ kind, title, subtitle, id: Date.now() });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(endpoint);
      const body = response.data;
      const payload = body && body.data ? body.data : body;
      setData(normalizeData(payload));
    } catch (err) {
      if (err?.response?.status === 404) {
        setData(normalizeData(null));
      } else {
        console.error('Greška pri učitavanju zadataka:', err);
        setError('Greška pri učitavanju zadataka');
      }
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Selekcija i otvoreni panel sa detaljima se resetuju pri promeni taba
  useEffect(() => {
    setSelectedIds([]);
    setDetailsTask(null);
  }, [activeTab]);

  // Na mobilnom: dok je neki zadatak označen ili je otvoren panel sa detaljima,
  // sakrij "Idi na vrh" strelicu (FAB-ovi/panel su već prikazani, strelica smeta).
  useEffect(() => {
    const active = selectedIds.length > 0 || detailsTask != null;
    document.body.classList.toggle('workday-tasks--actions-active', active);
    return () => {
      document.body.classList.remove('workday-tasks--actions-active');
    };
  }, [selectedIds, detailsTask]);


  const mine = useMemo(
    () => (data ? [...flattenEnriched(data.assignedToMe), ...flattenEnriched(data.assignedToMeInPast)] : []),
    [data]
  );
  const available = useMemo(() => (data ? flattenEnriched(data.unassigned) : []), [data]);
  const completed = useMemo(() => (data ? flattenEnriched(data.myCompletedTasks) : []), [data]);

  const tabBase = activeTab === 'mine' ? mine : activeTab === 'available' ? available : completed;

  // Pretraga zadataka po imenu korisnika, naslovu zadatka ili sobi
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tabBase;
    return tabBase.filter((task) => {
      const haystack = [getResidentName(task), getTaskTitle(task), getRoomName(task), getTaskSubtitle(task)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [tabBase, search]);

  const filteredGroups = useMemo(() => regroup(filtered), [filtered]);
  // Zadatke koje obuhvata akcija "Označi prikazane" / "Označi sve" čine samo
  // zadaci iz razvijenih kategorija - sklopljene kategorije se preskaču, pa
  // njihovi zadaci ne ulaze u masovnu selekciju.
  const visibleIds = useMemo(
    () =>
      filteredGroups
        .filter((group, index) => expandedCategories.has(categoryKey(group, index)))
        .flatMap((group) => (Array.isArray(group.tasks) ? group.tasks : []))
        .map((task) => task?.id)
        .filter((id) => id != null),
    [filteredGroups, expandedCategories]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const unassignedIdSet = useMemo(() => new Set(available.map((t) => t.id)), [available]);
  const hasUnassignedSelection = selectedIds.some((id) => unassignedIdSet.has(id));

  const tabCount = (id) =>
    (id === 'mine' ? mine : id === 'available' ? available : completed).length;

  const countLabel = () => {
    const n = filtered.length;
    if (activeTab === 'available') {
      return `${n} ${pluralForm(n, 'dostupan', 'dostupna', 'dostupnih')} ${pluralForm(n, 'zadatak', 'zadatka', 'zadataka')}`;
    }
    if (activeTab === 'mine') {
      return `${n} ${pluralForm(n, 'moj', 'moja', 'mojih')} ${pluralForm(n, 'zadatak', 'zadatka', 'zadataka')}`;
    }
    return `${n} ${pluralForm(n, 'završen', 'završena', 'završenih')} ${pluralForm(n, 'zadatak', 'zadatka', 'zadataka')}`;
  };

  const toggleTask = useCallback((id) => {
    if (id == null) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const clearSelection = () => setSelectedIds([]);

  // Otvaranje/zatvaranje desnog panela sa detaljima zadatka
  const openTaskDetails = useCallback((task) => setDetailsTask(task), []);
  const closeTaskDetails = useCallback(() => setDetailsTask(null), []);

  // Pretraga - identično ponašanje kao opšta (globalna) pretraga:
  // dugme "x" briše unos i vraća fokus, Escape briše unos i sklanja fokus.
  const clearSearch = () => {
    setSearch('');
    searchInputRef.current?.focus();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSearch('');
      searchInputRef.current?.blur();
    }
  };

  const toggleCategory = (key) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  const handleAction = async () => {
    if (selectedIds.length === 0 || saving) return;
    setSaving(true);
    const count = selectedIds.length;
    try {
      if (hasUnassignedSelection) {
        // Preuzimanje (dodela sebi) - isti poziv kao na stranici zadataka
        await axiosInstance.post('/api/Task/assign', {
          taskIds: selectedIds,
          employeeId: '00000000-0000-0000-0000-000000000000',
        });
        showToast(
          'success',
          'Zadaci preuzeti',
          `${count} ${pluralForm(count, 'zadatak', 'zadatka', 'zadataka')}.`
        );
      } else {
        await axiosInstance.post('/api/Task/complete-tasks', { taskIds: selectedIds });
        showToast(
          'success',
          'Zadaci završeni',
          `${count} ${pluralForm(count, 'zadatak', 'zadatka', 'zadataka')}.`
        );
      }
      setSelectedIds([]);
      setDetailsTask(null);
      await fetchTasks();
    } catch (err) {
      console.error('Greška pri izvršavanju akcije nad zadacima:', err);

      if (isConcurrencyConflictError(err)) {
        // Neko je u međuvremenu izmenio zadatke — osveži prikaz da korisnik vidi
        // stvarno stanje umesto zastarele liste.
        await fetchTasks();
      }

      showToast('error', 'Greška', getApiErrorMessage(err, 'Pokušajte ponovo.'));
    } finally {
      setSaving(false);
    }
  };


  const selectable = activeTab !== 'completed';

  return (
    <section className="wdash-panel wdash-tasks">
      <button
        type="button"
        className="wdash-tasks__toggle"
        onClick={() => setIsPanelCollapsed((prev) => !prev)}
        aria-expanded={!isPanelCollapsed}
      >
        <span className="wdash-tasks__head-icon">
          <CheckboxChecked size={20} />
        </span>
        <span className="wdash-tasks__head-text">
          <span className="wdash-tasks__title">Zadaci danas</span>
          <span className="wdash-tasks__subtitle">Pregled i preuzimanje zadataka</span>
        </span>
        <ChevronDown
          size={20}
          className={`wdash-tasks__chevron ${isPanelCollapsed ? '' : 'is-expanded'}`}
        />
      </button>

      <Collapsible open={!isPanelCollapsed}>
        <div className="wdash-tasks__body">
          {/* Tabovi */}
          <div className="wdash-tabs" role="tablist">
        {TABS.map((tab) => {
          const count = tabCount(tab.id);
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`wdash-tab ${isActive ? 'wdash-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
              <span className="wdash-tab__count">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filteri (pretraga) - izgled i ponašanje kao opšta pretraga u zaglavlju */}
      <div className="wdash-tasks__filters">
        <div className="wdash-tasks__search-field">
          <SearchIcon size={16} className="wdash-tasks__search-icon" aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="search"
            className="wdash-tasks__search-input"
            placeholder="Pretraži korisnika..."
            aria-label="Pretraga zadataka"
            autoComplete="off"
            maxLength={100}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {search && (
            <button
              type="button"
              className="wdash-tasks__search-clear"
              aria-label="Obriši pretragu"
              onClick={clearSearch}
            >
              <Close size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Podnaslov */}
      <div className="wdash-tasks__subhead">
        <span className="wdash-tasks__count-label">{countLabel()}</span>
        {selectable && visibleIds.length > 0 && (
          <button type="button" className="wdash-tasks__select" onClick={toggleSelectAllVisible}>
            {allVisibleSelected ? 'Poništi prikazane' : 'Označi prikazane'}
          </button>
        )}
      </div>


      {/* Lista zadataka po kategorijama */}
      <div className="wdash-tasks__list">
        {loading ? (
          <div className="wdash-tasks__skeleton">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="wdash-tasks__empty">{error}</div>
        ) : filteredGroups.length === 0 ? (
          <div className="wdash-tasks__empty">Nema zadataka</div>
        ) : (
          filteredGroups.map((group, index) => {
            const key = categoryKey(group, index);
            const isCollapsed = !expandedCategories.has(key);
            const CategoryIcon = categoryIcon(group.category);
            return (
              <div className="wdash-cat" key={key}>
                <button
                  type="button"
                  className={`wdash-cat__head ${isCollapsed ? '' : 'is-expanded'}`}
                  onClick={() => toggleCategory(key)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="wdash-cat__icon">
                    <CategoryIcon size={18} />
                  </span>
                  <span className="wdash-cat__title">{group.category || 'Zadaci'}</span>
                  <span className="wdash-cat__count">{group.tasks.length}</span>
                  <ChevronDown
                    size={18}
                    className={`wdash-cat__chevron ${isCollapsed ? '' : 'is-expanded'}`}
                  />
                </button>

                <Collapsible open={!isCollapsed}>
                  <div className="wdash-cat__items">
                    {group.tasks.map((task, taskIndex) => (
                      <TaskRow
                        key={task.id || `${key}-${taskIndex}`}
                        task={task}
                        selectable={selectable}
                        selected={selectedIds.includes(task.id)}
                        active={detailsTask?.id === task.id}
                        onToggle={toggleTask}
                        onOpen={openTaskDetails}
                      />
                    ))}
                  </div>
                </Collapsible>
              </div>
            );
          })
        )}
          </div>
        </div>
      </Collapsible>

      {/* Traka selekcije */}
      {selectedIds.length > 0 && (
        <div className="wdash-selection">
          <span className="wdash-selection__info">
            {selectedIds.length === 1
              ? 'Izabran 1 zadatak'
              : `Izabrana ${selectedIds.length} ${pluralForm(selectedIds.length, 'zadatak', 'zadatka', 'zadataka')}`}
          </span>
          <div className="wdash-selection__actions">
            <button
              type="button"
              className="wdash-selection__clear"
              onClick={clearSelection}
              disabled={saving}
            >
              Poništi
            </button>
            <button
              type="button"
              className="wdash-selection__primary"
              onClick={handleAction}
              disabled={saving}
            >
              {saving
                ? 'Sačekajte...'
                : hasUnassignedSelection
                ? `Preuzmi ${selectedIds.length} ${pluralForm(selectedIds.length, 'zadatak', 'zadatka', 'zadataka')}`
                : `Završi ${selectedIds.length} ${pluralForm(selectedIds.length, 'zadatak', 'zadatka', 'zadataka')}`}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="wdash-toast">
          <ToastNotification
            kind={toast.kind}
            title={toast.title}
            subtitle={toast.subtitle}
            lowContrast
            timeout={0}
            onClose={() => setToast(null)}
          />
        </div>
      )}

      {/* Lebdeći (FAB) akcioni dugmići - na PC-u stalno dostupni, na mobilnom
          se prikazuju kad je nešto izabrano. */}
      {(selectedIds.length > 0 || (isDesktopView && visibleIds.length > 0)) && ReactDOM.createPortal(
        <Theme theme={theme}>
          {/* "Označi sve" se prikazuje samo ako postoji bar jedan zadatak u
              razvijenoj kategoriji (sklopljene kategorije se ne obuhvataju). */}
          {visibleIds.length > 0 && (isDesktopView || selectedIds.length > 0) && (
            <button
              type="button"
              className="wdash-fab wdash-fab--select-all"
              onClick={allVisibleSelected ? clearSelection : toggleSelectAllVisible}
              aria-label={allVisibleSelected ? 'Poništi izbor' : 'Označi sve zadatke'}
            >
              <CheckboxChecked size={22} />
              <span>{allVisibleSelected ? 'Poništi sve' : 'Označi sve'}</span>
            </button>
          )}

          {selectedIds.length > 0 && (
            <button
              type="button"
              className="wdash-fab wdash-fab--primary"
              onClick={handleAction}
              disabled={saving}
              aria-label={hasUnassignedSelection ? 'Preuzmi zadatke' : 'Završi zadatke'}
            >
              <Checkmark size={22} />
              <span>
                {saving
                  ? 'Sačekajte...'
                  : hasUnassignedSelection
                  ? `Preuzmi (${selectedIds.length})`
                  : `Završi (${selectedIds.length})`}
              </span>
            </button>
          )}
        </Theme>,
        document.body
      )}

      {/* Desni panel sa detaljima izabranog zadatka (klik na red) */}
      <TaskDetailsDrawer task={detailsTask} onClose={closeTaskDetails} />

    </section>
  );
};

export default WorkDayTasks;

